import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Input,
  Card,
  Drawer,
  Tag,
  Avatar,
  notification,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { customerService } from '../../services/customerService';
import CustomerForm from './CustomerForm';

const { Column } = Table;
const { Search } = Input;

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [recentlyAddedCount, setRecentlyAddedCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [prefillCustomer, setPrefillCustomer] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchCustomers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await customerService.getAllCustomers({
        page,
        limit: pagination.pageSize,
        search
      });

      setCustomers(Array.isArray(response?.data) ? response.data : []);
      setRecentlyAddedCount(response?.recentlyAddedCount || 0);

      setPagination(prev => ({
        ...prev,
        current: response?.pagination?.page || 1,
        total: response?.pagination?.total || (Array.isArray(response?.data) ? response.data.length : 0)
      }));
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch customers'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // If navigated from Leads (OrderFinalized), auto-open New Customer with prefill.
  useEffect(() => {
    const lead = location?.state?.prefillFromLead;
    if (!lead) return;

    const normalizedEmail = typeof lead.email === 'string' ? lead.email.trim() : '';
    const normalizedMobile = String(lead.contactNumber || lead.contact || '').trim();
    const normalizedPincode = String(lead.pincode || '').trim();

    setSelectedCustomer(null);
    setPrefillCustomer({
      customerName: lead.fullName || '',
      customerEmail: normalizedEmail || null,
      customerMobile: normalizedMobile || '',
      customerAddress: lead.address || '',
      district: lead.city || '',
      pincode: normalizedPincode || ''
    });
    setIsDrawerVisible(true);

    // Clear state so refresh/back doesn't reopen the drawer
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  const handleTableChange = (pagination) => {
    fetchCustomers(pagination.current, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCustomers(1, value);
  };

  const handleAdd = () => {
    setSelectedCustomer(null);
    setPrefillCustomer(null);
    setIsDrawerVisible(true);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setPrefillCustomer(null);
    setIsDrawerVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await customerService.deleteCustomer(id);
      notification.success({
        message: 'Success',
        description: 'Customer deleted successfully'
      });
      fetchCustomers(pagination.current, searchText);
    } catch (error) {
      console.error('Delete customer error:', error);
      const errorMessage = error?.error || error?.message || 'Failed to delete customer. Please check your permissions or try again.';
      notification.error({
        message: 'Delete Failed',
        description: errorMessage,
        duration: 5
      });
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setSelectedCustomer(null);
    setPrefillCustomer(null);
  };

  const handleFormSubmit = () => {
    handleDrawerClose();
    fetchCustomers(pagination.current, searchText);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row gutter={[20, 20]} align="stretch">
        {[
          { title: 'Total Customers', value: pagination.total || 0, icon: <UsergroupAddOutlined />, color: '#1890ff', bg: '#e6f7ff', description: null },
          {
            title: 'Recently Added',
            value: recentlyAddedCount,
            icon: <UserOutlined />,
            color: '#52c41a',
            bg: '#f6ffed',
            description: 'Customers added in the last 30 days'
          },
        ].map((item, index) => (
          <Col xs={24} sm={12} key={index} style={{ display: 'flex' }}>
            <Card bordered={false} style={{
              borderRadius: 16,
              background: item.bg,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              position: 'relative',
              flex: 1
            }}>
              <div style={{ position: 'absolute', right: -10, top: -10, opacity: 0.1, fontSize: 60, color: item.color }}>
                {item.icon}
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Typography.Text type="secondary" style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                  {item.title}
                </Typography.Text>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Typography.Text style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                    {item.value}
                  </Typography.Text>
                </div>
                {item.description && (
                  <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                    {item.description}
                  </Typography.Text>
                )}
                <div style={{ marginTop: 4 }}>
                  <Tag color={item.color} style={{ borderRadius: 10, border: 'none', fontSize: 10 }}>Live Data</Tag>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={<Space><UserOutlined style={{ fontSize: 18, color: '#1890ff' }} /><span>Manage Customers</span></Space>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            New Customer
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search customers by name, mobile or email..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            style={{ width: '100%', maxWidth: 500 }}
          />
        </div>
        <Table
          dataSource={customers}
          loading={loading}
          rowKey="id"
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1000 }}
        >
          <Column
            title="Customer"
            key="name"
            fixed="left"
            render={(record) => (
              <Space>
                <Avatar
                  size="large"
                  style={{ backgroundColor: '#1890ff' }}
                  icon={<UserOutlined />}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{record.customerName}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {record.customerEmail}
                  </div>
                </div>
              </Space>
            )}
          />

          <Column
            title="Mobile"
            dataIndex="customerMobile"
            key="customerMobile"
          />

          <Column
            title="Consumer No"
            key="consumerNo"
            render={(record) => {
              const numbers = record.consumerNumbers?.map(cn => cn.consumerNumber) || [];
              // Fallback to the legacy field if the relation array is empty
              if (numbers.length === 0 && record.consumerNo) {
                numbers.push(record.consumerNo);
              }
              return (
                <Space direction="vertical" size={0}>
                  {numbers.map((num, idx) => (
                    <Tag color="blue" key={idx} style={{ margin: '2px 0' }}>{num}</Tag>
                  ))}
                </Space>
              );
            }}
          />

          <Column
            title="District"
            dataIndex="district"
            key="district"
          />

          <Column
            title="Pin Code"
            dataIndex="pincode"
            key="pincode"
          />

          <Column
            title="Location"
            key="location"
            render={(record) => (
              <Tag color={record.locationType === 'Urban' ? 'blue' : 'cyan'} style={{ borderRadius: '10px' }}>
                {record.locationType}
              </Tag>
            )}
          />

          <Column
            title="House Ownership"
            key="houseOwnership"
            render={(record) => (
              <Tag color={record.houseOwnership === 'Single' ? 'purple' : 'orange'} style={{ borderRadius: '10px' }}>
                {record.houseOwnership}
              </Tag>
            )}
          />

          <Column
            title="Actions"
            key="actions"
            fixed="right"
            render={(record) => (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined style={{ color: '#1890ff' }} />}
                  onClick={() => handleEdit(record)}
                />
                <Popconfirm
                  title="Delete Customer"
                  description="Are you sure you want to delete this customer? Customers with linked orders cannot be deleted."
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </Card>

      <Drawer
        title={selectedCustomer ? 'Edit Customer' : 'New Customer'}
        width={720}
        onClose={handleDrawerClose}
        open={isDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        destroyOnClose
      >
        <CustomerForm
          customer={selectedCustomer}
          prefill={!selectedCustomer ? prefillCustomer : null}
          onSuccess={handleFormSubmit}
          onCancel={handleDrawerClose}
        />
      </Drawer>
    </Space>
  );
};

export default CustomerList;